(function () {
  'use strict';
  const Fit = typeof module !== 'undefined' && module.exports ? require('./fitwarm-fit-engine.js') : window.FitwarmFit;
  const EPSILON = 1e-7;
  const LABELS = { back: 'Back length', waist: 'Waist girth', neck: 'Neck girth' };
  const HINTS = { back: 'From neck base to tail base, to check coverage.', waist: 'Measure snugly around the waist, without adding room.', neck: 'Around the neck, with two fingers of room.' };
  // Broad measurement bounds, independent of the clothing charts. Valid dogs
  // outside a product's sizes must still reach the existing fit-advice result.
  const INPUT_RANGES_IN = { chest: [4, 60], back: [2, 40], waist: [4, 60], neck: [3, 40] };
  const ALIASES = { yorkie: 'Yorkshire Terrier', 'yorkshire terrier': 'Yorkshire Terrier', 'mini dachshund': 'Miniature Dachshund', 'dachshund miniature': 'Miniature Dachshund', 'dachshund standard': 'Standard Dachshund', frenchie: 'French Bulldog', westie: 'West Highland White Terrier', scottie: 'Scottish Terrier', 'jack russell': 'Jack Russell Terrier', corgis: 'Corgi', 'welsh corgi': 'Corgi', husky: 'Siberian Husky', 'miniature schnauzer': 'Schnauzer', doberman: 'Dobermann', 'toy poodle': 'Toy Poodle', 'poodle toy': 'Toy Poodle', 'teacup yorkie': 'Teacup Yorkshire Terrier' };
  const normalize = value => value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const pretty = value => Number(Number(value).toFixed(2)).toString();
  function measurementRange(kind, unit) {
    if (!INPUT_RANGES_IN[kind] || !['in', 'cm'].includes(unit)) throw new Error('Choose a measurement and unit.');
    const factor = unit === 'cm' ? 2.54 : 1;
    return INPUT_RANGES_IN[kind].map(value => Number((value * factor).toFixed(2)));
  }
  function measurementInputIssue(text, kind, unit, allowPartial = false) {
    if (text === '' && allowPartial) return '';
    if (!/^(?:\d+(?:\.\d{0,2})?|\.\d{1,2})$/.test(text)) return 'format';
    const value = Number(text);
    const [min, max] = measurementRange(kind, unit);
    return !Number.isFinite(value) || value > max + EPSILON || (!allowPartial && value < min - EPSILON) ? 'range' : '';
  }
  function recommend(chart, measurements, unit) {
    if (!chart || !['in', 'cm'].includes(unit)) throw new Error('Choose a clothing category and unit.');
    const column = unit === 'cm' ? 1 : 0;
    const bandana = chart.secondary === 'neck';
    const hasSecondary = measurements.secondary !== null && measurements.secondary !== undefined;
    const keys = bandana ? ['secondary'] : ['chest', ...(chart.secondary === 'waist' || hasSecondary ? ['secondary'] : [])];
    if (keys.some(key => typeof measurements[key] !== 'number' || !Number.isFinite(measurements[key]) || measurements[key] <= 0)) throw new Error('Enter a measurement greater than zero for each field.');
    for (const key of keys) {
      const kind = key === 'chest' ? 'chest' : chart.secondary;
      const [min, max] = measurementRange(kind, unit);
      if (measurements[key] < min - EPSILON || measurements[key] > max + EPSILON) throw new Error(`${key === 'chest' ? 'Chest girth' : LABELS[kind]} must be between ${min} and ${max} ${unit}.`);
    }
    const { chest, secondary } = measurements;
    const rows = chart.rows;
    if (bandana) {
      const matches = rows.filter(row => secondary >= row.neckMin[column] - EPSILON && secondary <= row.neckMax[column] + EPSILON);
      return matches.length ? { status: 'fit', row: matches[0], alternatives: matches.slice(1), note: 'Start with the smallest listed neck range that fits. Keep two fingers of room and check the product’s tying instructions.' } : { status: 'outside', note: 'This neck measurement is outside the listed bandana ranges. Please check the product’s chart or ask us for help before ordering.' };
    }
    // Fit against finished garment centimetres, not a maximum dog measurement.
    // Back length is coverage advice, including for long or broad-chested dogs.
    const factor = unit === 'cm' ? 1 : 2.54;
    const match = Fit.chestFit(rows, chest * factor, chart.secondary === 'waist' ? secondary * factor : null);
    if (!match.row) return { ...match, status: 'outside', note: 'Your body measurements plus 2.5 cm of room exceed this category’s garment sizes. Check another style or ask us for sizing help.' };
    let note = chart.secondary === 'back' && hasSecondary ? Fit.backCoverage(match.row, secondary * factor) : '';
    if (match.row === rows[0] && match.requiredChestCm < Fit.garmentValue(rows[0], 'chest') - 3) note += ' This is the smallest listed size and may be loose.';
    return { ...match, status: 'fit', note: note.trim() };
  }
  function breedSizes(chart, breed) {
    const key = normalize(breed);
    return chart.rows.filter(row => (chart.breeds[row.size] || '').split('|').some(name => normalize(name) === key)).map(row => row.size);
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { recommend, breedSizes, normalize, measurementRange, measurementInputIssue };
  if (typeof window === 'undefined' || !window.customElements || customElements.get('fitwarm-size-guide')) return;

  // Reuse the existing puppy calculator chevron and theme close icon.
  const CHEVRON = '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false"><path d="m5 7.5 5 5 5-5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  class SizeSelect {
    constructor(select, owner) {
      this.select = select;
      this.owner = owner;
      this.label = owner.querySelector(`label[for="${select.id}"]`);
      this.originalLabelId = this.label.id;
      this.label.id = `${select.id}-label`;
      this.wrapper = document.createElement('div');
      this.wrapper.className = 'fw-size__select';
      select.before(this.wrapper);
      this.wrapper.append(select);
      select.hidden = true;
      this.trigger = document.createElement('button');
      this.trigger.type = 'button';
      this.trigger.className = 'fw-size__select-trigger';
      this.trigger.id = `${select.id}-control`;
      this.trigger.setAttribute('role', 'combobox');
      this.trigger.setAttribute('aria-haspopup', 'listbox');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.value = document.createElement('span');
      this.value.id = `${select.id}-value`;
      this.value.className = 'fw-size__select-value';
      this.trigger.append(this.value);
      this.trigger.insertAdjacentHTML('beforeend', CHEVRON);
      this.trigger.setAttribute('aria-labelledby', `${this.label.id} ${this.value.id}`);
      this.label.htmlFor = this.trigger.id;
      this.panel = document.createElement('div');
      this.panel.className = 'fw-size__select-panel';
      this.panel.id = `${select.id}-listbox`;
      this.panel.setAttribute('role', 'listbox');
      this.panel.setAttribute('aria-labelledby', this.label.id);
      this.panel.hidden = true;
      this.trigger.setAttribute('aria-controls', this.panel.id);
      this.options = [...select.options].map((option, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'fw-size__select-option';
        button.id = `${select.id}-option-${index}`;
        button.dataset.index = String(index);
        button.tabIndex = -1;
        button.setAttribute('role', 'option');
        button.setAttribute('aria-selected', String(option.selected));
        button.textContent = option.textContent.trim();
        button.disabled = option.disabled;
        this.panel.append(button);
        return button;
      });
      this.wrapper.append(this.trigger, this.panel);
      owner.listen(this.trigger, 'click', () => this.isOpen ? this.close() : this.open());
      owner.listen(this.trigger, 'keydown', event => this.keydown(event));
      owner.listen(this.panel, 'pointerdown', event => { if (event.target.closest('[role="option"]')) event.preventDefault(); });
      owner.listen(this.panel, 'pointermove', event => {
        const option = event.target.closest('[role="option"]');
        if (option && !option.disabled) { this.active = Number(option.dataset.index); this.highlight(false); }
      });
      owner.listen(this.panel, 'click', event => {
        const option = event.target.closest('[role="option"]');
        if (option && !option.disabled) this.choose(Number(option.dataset.index));
      });
      owner.listen(select, 'change', () => this.sync());
      owner.listen(this.wrapper, 'focusout', event => {
        if (!this.wrapper.contains(event.relatedTarget)) this.close();
      });
      owner.listen(document, 'pointerdown', event => {
        if (this.isOpen && !this.wrapper.contains(event.target)) this.close();
      });
      owner.listen(window, 'resize', () => { if (this.isOpen) this.positionPanel(); });
      if (window.visualViewport) owner.listen(window.visualViewport, 'resize', () => { if (this.isOpen) this.positionPanel(); });
      this.sync();
    }
    sync() {
      this.value.textContent = this.select.selectedOptions[0]?.textContent.trim() || '';
      this.trigger.disabled = this.select.disabled;
      this.active = Math.max(0, this.select.selectedIndex);
      this.options.forEach((option, index) => option.setAttribute('aria-selected', String(index === this.select.selectedIndex)));
      this.highlight(false);
    }
    highlight(scroll = true) {
      this.options.forEach((option, index) => option.classList.toggle('is-active', index === this.active));
      const active = this.options[this.active];
      if (this.isOpen && active) {
        this.trigger.setAttribute('aria-activedescendant', active.id);
        if (scroll) {
          const top = active.offsetTop;
          const bottom = top + active.offsetHeight;
          if (top < this.panel.scrollTop) this.panel.scrollTop = top;
          else if (bottom > this.panel.scrollTop + this.panel.clientHeight) this.panel.scrollTop = bottom - this.panel.clientHeight;
        }
      } else this.trigger.removeAttribute('aria-activedescendant');
    }
    positionPanel() {
      const rect = this.trigger.getBoundingClientRect();
      const viewport = window.visualViewport;
      const viewportBottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
      const headerBottom = Math.max(0, document.querySelector('#SiteHeader')?.getBoundingClientRect().bottom || 0);
      const navigation = this.owner.navigation?.getBoundingClientRect();
      const navigationBottom = navigation && navigation.bottom > 0 && navigation.top < viewportBottom ? navigation.bottom : 0;
      const above = Math.max(0, rect.top - Math.max(viewport?.offsetTop || 0, headerBottom, navigationBottom) - 12);
      const below = Math.max(0, viewportBottom - rect.bottom - 12);
      const needed = Math.min(280, this.panel.scrollHeight);
      const upward = below < needed && above > below;
      this.wrapper.classList.toggle('opens-up', upward);
      this.panel.style.maxHeight = `${Math.min(280, Math.max(60, upward ? above : below))}px`;
    }
    open() {
      if (this.trigger.disabled) return;
      this.owner.selectControls.forEach(control => { if (control !== this) control.close(); });
      this.sync();
      this.isOpen = true;
      this.panel.hidden = false;
      this.wrapper.classList.add('is-open');
      this.trigger.setAttribute('aria-expanded', 'true');
      this.positionPanel();
      this.highlight();
    }
    close(returnFocus = false) {
      this.isOpen = false;
      this.panel.hidden = true;
      this.wrapper.classList.remove('is-open');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.trigger.removeAttribute('aria-activedescendant');
      this.typeahead = '';
      if (returnFocus) this.trigger.focus({ preventScroll: true });
    }
    choose(index) {
      if (!this.options[index] || this.options[index].disabled) return;
      this.select.selectedIndex = index;
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
      this.sync();
      this.close(true);
    }
    keydown(event) {
      if (event.key === 'Tab') { this.close(); return; }
      if (event.key === 'Escape' && this.isOpen) { event.preventDefault(); event.stopPropagation(); this.close(true); return; }
      if (['Enter', ' '].includes(event.key)) {
        event.preventDefault();
        if (this.isOpen) this.choose(this.active);
        else this.open();
        return;
      }
      if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        const wasOpen = this.isOpen;
        if (!wasOpen) this.open();
        const enabled = this.options.map((option, index) => option.disabled ? -1 : index).filter(index => index >= 0);
        if (event.key === 'Home') this.active = enabled[0];
        else if (event.key === 'End') this.active = enabled[enabled.length - 1];
        else if (wasOpen) {
          const next = Math.max(0, Math.min(enabled.length - 1, enabled.indexOf(this.active) + (event.key === 'ArrowDown' ? 1 : -1)));
          this.active = enabled[next];
        }
        this.highlight();
        return;
      }
      if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
        event.preventDefault();
        const now = Date.now();
        const query = now - (this.typedAt || 0) < 700 ? (this.typeahead || '') + event.key.toLowerCase() : event.key.toLowerCase();
        if (!this.isOpen) this.open();
        this.typeahead = query;
        this.typedAt = now;
        const match = this.options.findIndex(option => !option.disabled && option.textContent.toLowerCase().startsWith(query));
        if (match >= 0) { this.active = match; this.highlight(); }
      }
    }
    destroy() {
      this.close();
      this.select.hidden = false;
      this.label.htmlFor = this.select.id;
      this.label.id = this.originalLabelId;
      this.wrapper.replaceWith(this.select);
    }
  }

  class FitwarmSizeGuide extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.abort = new AbortController();
      this.data = JSON.parse(this.querySelector('[data-size-data]').textContent);
      this.form = this.querySelector('[data-size-form]');
      this.unit = 'in';
      this.chartUnit = 'in';
      this.chartCategory = 'everyday';
      this.secondaryType = 'back';
      this.acceptedInputs = { chest: this.form.elements.chest.value, secondary: this.form.elements.secondary.value };
      this.selectedBreed = '';
      this.breeds = [...new Set(Object.values(this.data).flatMap(chart => Object.values(chart.breeds).flatMap(names => names.split('|'))))].sort();
      this.listen(this.form, 'submit', event => { event.preventDefault(); this.calculate(); });
      this.listen(this.form, 'input', event => {
        if (!['chest', 'secondary'].includes(event.target.name)) return;
        const field = event.target;
        const issue = measurementInputIssue(field.value, this.measurementKind(field), this.unit, true);
        if (issue) field.value = this.acceptedInputs[field.name];
        else this.acceptedInputs[field.name] = field.value;
        this.basis = this.readMeasurements();
        field.removeAttribute('aria-invalid');
        this.showError(issue ? this.inputError(field, issue) : '');
      });
      this.listen(this.form, 'beforeinput', event => {
        if (event.target.type === 'number' && event.data && /[eE+\-]/.test(event.data)) event.preventDefault();
      });
      this.listen(this.form, 'keydown', event => {
        if (event.target.type === 'number' && !event.ctrlKey && !event.metaKey && ['e', 'E', '+', '-'].includes(event.key)) event.preventDefault();
      });
      this.listen(this.form, 'paste', event => {
        const field = event.target;
        if (field.type !== 'number') return;
        const text = event.clipboardData?.getData('text');
        if (text === undefined) return;
        event.preventDefault();
        const raw = text.trim();
        const issue = measurementInputIssue(raw, this.measurementKind(field), this.unit);
        if (!issue) {
          field.value = raw;
          this.acceptedInputs[field.name] = field.value;
          field.removeAttribute('aria-invalid');
        }
        this.basis = this.readMeasurements();
        this.showError(issue ? this.inputError(field, issue) : '');
      });
      this.listen(this.form, 'focusout', event => {
        const field = event.target;
        if (field.type !== 'number' || (field.value === '' && !field.validity.badInput)) return;
        const issue = measurementInputIssue(field.value, this.measurementKind(field), this.unit);
        if (issue) {
          field.setAttribute('aria-invalid', 'true');
          this.showError(this.inputError(field, issue));
        }
      });
      this.listen(this.form, 'change', event => {
        if (event.target.name === 'unit') this.setUnit(event.target.value);
        if (event.target.name === 'category') this.setCategory();
      });
      this.querySelectorAll('[data-chart-category]').forEach(button => {
        this.listen(button, 'click', () => this.showChart(button.dataset.chartCategory));
        this.listen(button, 'keydown', event => {
          const buttons = [...this.querySelectorAll('[data-chart-category]')];
          const index = buttons.indexOf(button);
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % buttons.length;
          if (event.key === 'ArrowLeft') next = (index + buttons.length - 1) % buttons.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = buttons.length - 1;
          if (next === undefined) return;
          event.preventDefault();
          this.showChart(buttons[next].dataset.chartCategory);
          buttons[next].focus();
        });
      });
      this.querySelectorAll('input[name^="chart-unit-"]').forEach(input => this.listen(input, 'change', () => { this.chartUnit = input.value; this.renderCharts(); }));
      this.listen(this.querySelector('[data-breed-search]'), 'input', () => {
        this.selectedBreed = '';
        this.querySelector('[data-breed-result]').hidden = true;
        this.renderBreedOptions();
      });
      this.listen(this.querySelector('[data-breed-clear]'), 'click', () => {
        const search = this.querySelector('[data-breed-search]');
        search.value = '';
        this.selectedBreed = '';
        this.querySelector('[data-breed-result]').hidden = true;
        this.renderBreedOptions();
        search.focus({ preventScroll: true });
      });
      this.listen(this.querySelector('[data-breed-category]'), 'change', () => this.renderBreedResult());
      this.listen(this.querySelector('[data-refine]'), 'click', () => {
        this.form.elements.category.value = this.querySelector('[data-breed-category]').value;
        this.setCategory();
        this.scrollToSection(this.navigationSections[0]);
        (this.form.elements.category.value === 'bandana' ? this.form.elements.secondary : this.form.elements.chest).focus({ preventScroll: true });
      });
      this.querySelector('[data-chart-tabs]').hidden = false;
      this.selectControls = [...this.querySelectorAll('select')].map(select => new SizeSelect(select, this));
      this.querySelectorAll('.fw-size__faqs summary').forEach(summary => summary.insertAdjacentHTML('beforeend', CHEVRON));
      this.querySelectorAll('[data-chart-panel]').forEach(panel => { panel.setAttribute('role', 'tabpanel'); panel.setAttribute('aria-labelledby', `SizeTab-${panel.dataset.chartPanel}-${this.id.replace('FitwarmSize-', '')}`); });
      this.updateInputLimits();
      this.renderCharts();
      this.showChart('everyday');
      this.renderBreedOptions();
      this.classList.add('is-ready');
      this.setupNavigation();
    }
    disconnectedCallback() {
      if (this.abort) this.abort.abort();
      this.selectControls?.forEach(control => control.destroy());
      this.querySelectorAll('.fw-size__faqs summary>svg').forEach(icon => icon.remove());
      this.navigationResize?.disconnect();
      this.navigationHeaderObserver?.disconnect();
      if (this.navigationFrame) cancelAnimationFrame(this.navigationFrame);
      this.navigationFrame = null;
      this.ready = false;
    }
    listen(node, name, fn, options = {}) { node.addEventListener(name, fn, { ...options, signal: this.abort.signal }); }
    text(selector, value) { this.querySelector(selector).textContent = value; }
    motion() { return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'; }
    setupNavigation() {
      this.navigation = this.querySelector('[data-size-navigation]');
      this.navigationLinks = [...this.navigation.querySelectorAll('a')];
      this.navigationSections = this.navigationLinks.map(link => document.getElementById(link.hash.slice(1)));
      this.navigationHeader = document.querySelector('#SiteHeader');
      const schedule = () => {
        if (this.navigationFrame) return;
        this.navigationFrame = requestAnimationFrame(() => {
          this.navigationFrame = null;
          this.updateNavigation();
        });
      };
      this.listen(window, 'scroll', schedule, { passive: true });
      this.listen(window, 'resize', schedule, { passive: true });
      this.listen(this, 'click', event => {
        if (event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        const link = event.target.closest('a[href^="#"]');
        if (!link || link.target === '_blank') return;
        const target = document.getElementById(link.hash.slice(1));
        if (!target || !this.contains(target)) return;
        event.preventDefault();
        this.scrollToSection(target);
        if (window.location.hash !== link.hash) window.history.pushState(null, '', link.hash);
        const heading = target.querySelector('h2, h3') || target;
        heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
      });
      if (typeof ResizeObserver !== 'undefined') {
        this.navigationResize = new ResizeObserver(schedule);
        for (const node of [this, this.navigation, this.navigationHeader]) if (node) this.navigationResize.observe(node);
      }
      if (this.navigationHeader) {
        this.navigationHeaderObserver = new MutationObserver(schedule);
        this.navigationHeaderObserver.observe(this.navigationHeader, { attributes: true, attributeFilter: ['class', 'style'] });
      }
      this.updateNavigation();
    }
    updateNavigation() {
      const header = this.navigationHeader;
      const style = header && getComputedStyle(header);
      const fixed = style && ['fixed', 'sticky'].includes(style.position) && style.display !== 'none';
      const headerHeight = header ? header.getBoundingClientRect().height : 0;
      // Preserve fractional pixels and reserve the header's layout height
      // during its entrance transform so no seam opens above the navigation.
      this.navigationTop = fixed ? headerHeight + Math.max(0, parseFloat(style.top) || 0) : 0;
      const height = Math.ceil(this.navigation.getBoundingClientRect().height);
      const expectedHeader = fixed || header?.dataset.sticky === 'true' ? headerHeight : 0;
      this.navigationOffset = expectedHeader + height + 20;
      this.style.setProperty('--fs-header-offset', `${this.navigationTop}px`);
      this.style.setProperty('--fs-section-offset', `${this.navigationOffset}px`);
      const navRect = this.navigation.getBoundingClientRect();
      this.navigation.classList.toggle('is-stuck', navRect.top <= this.navigationTop + 1 && navRect.bottom > this.navigationTop);
      // The theme shrinks its header by 20px as it becomes fixed. Include
      // that small landing gap when deciding which anchored section is active.
      const activationLine = Math.max(this.navigationTop + height + 28, this.navigationOffset + 24);
      let active = 0;
      this.navigationSections.forEach((section, index) => { if (section.getBoundingClientRect().top <= activationLine) active = index; });
      this.navigationLinks.forEach((link, index) => {
        if (index === active) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
    }
    scrollToSection(target) {
      this.selectControls?.forEach(control => control.close());
      this.updateNavigation();
      const top = Math.max(0, window.scrollY + target.getBoundingClientRect().top - this.navigationOffset);
      window.scrollTo({ top, behavior: this.motion() });
    }
    measurementKind(field) { return field.name === 'chest' ? 'chest' : this.secondaryType; }
    inputError(field, issue) {
      const kind = this.measurementKind(field);
      const [min, max] = measurementRange(kind, this.unit);
      const label = kind === 'chest' ? 'chest girth' : LABELS[kind].toLowerCase();
      return `Enter a ${label} between ${min} and ${max} ${this.unit}.${issue === 'format' && field.value !== '' ? ' Use numbers with up to two decimal places.' : ''}`;
    }
    updateInputLimits() {
      for (const name of ['chest', 'secondary']) {
        const field = this.form.elements[name];
        const [min, max] = measurementRange(this.measurementKind(field), this.unit);
        field.min = String(min);
        field.max = String(max);
        field.step = '0.01';
        this.text(`[data-${name}-range]`, `${min}–${max} ${this.unit}.`);
        this.acceptedInputs[name] = field.value;
      }
    }
    readMeasurements() { return { unit: this.unit, chest: Number(this.form.elements.chest.value), secondary: this.form.elements.secondary.value === '' ? null : Number(this.form.elements.secondary.value) }; }
    showError(message) { this.text('[data-error]', message); this.querySelector('[data-error]').hidden = !message; }
    setCategory() {
      this.selectControls?.forEach(control => control.sync());
      const key = this.form.elements.category.value;
      const chart = this.data[key];
      const changedSecondary = this.secondaryType && this.secondaryType !== chart.secondary;
      this.secondaryType = chart.secondary;
      if (changedSecondary) { this.form.elements.secondary.value = ''; this.basis = null; }
      const bandana = key === 'bandana';
      this.querySelector('[data-chest-field]').hidden = bandana;
      this.form.elements.chest.disabled = bandana;
      this.form.elements.chest.required = !bandana;
      this.form.elements.secondary.required = chart.secondary !== 'back';
      this.text('[data-secondary-label]', `${LABELS[chart.secondary]}${chart.secondary === 'back' ? ' (optional)' : ''}`);
      this.text('[data-secondary-hint]', HINTS[chart.secondary]);
      this.form.elements.secondary.placeholder = this.unit === 'cm' ? (bandana ? 'e.g. 28' : 'e.g. 25') : (bandana ? 'e.g. 11' : 'e.g. 10');
      this.updateInputLimits();
      this.showError('');
      this.form.querySelectorAll('[aria-invalid]').forEach(input => input.removeAttribute('aria-invalid'));
      this.showChart(key);
    }
    setUnit(next) {
      if (next === this.unit) return;
      // Keep the originally entered measurement basis so switching units never
      // changes a recommendation because of display rounding.
      if (!this.basis) this.basis = this.readMeasurements();
      for (const key of ['chest', 'secondary']) {
        const input = this.form.elements[key];
        if (input.value !== '' && Number.isFinite(this.basis[key])) {
          const value = this.basis[key] * (this.basis.unit === next ? 1 : next === 'cm' ? 2.54 : 1 / 2.54);
          input.value = pretty(value);
        }
      }
      this.unit = next;
      this.querySelectorAll('[data-unit-label]').forEach(node => { node.textContent = next; });
      this.form.elements.chest.placeholder = next === 'cm' ? 'e.g. 36' : 'e.g. 14';
      this.form.elements.secondary.placeholder = next === 'cm' ? 'e.g. 25' : 'e.g. 10';
      this.updateInputLimits();
      this.form.querySelectorAll('[aria-invalid]').forEach(field => field.removeAttribute('aria-invalid'));
      const invalid = [...this.form.querySelectorAll('input[type="number"]')].find(field => !field.disabled && field.value !== '' && measurementInputIssue(field.value, this.measurementKind(field), next));
      this.showError(invalid ? this.inputError(invalid, 'range') : '');
      if (invalid) invalid.setAttribute('aria-invalid', 'true');
    }
    calculate() {
      const category = this.form.elements.category.value;
      const chart = this.data[category];
      const fields = category === 'bandana' ? [this.form.elements.secondary] : [this.form.elements.chest, ...(chart.secondary === 'waist' || this.form.elements.secondary.value !== '' ? [this.form.elements.secondary] : [])];
      fields.forEach(field => field.removeAttribute('aria-invalid'));
      const bad = fields.find(field => measurementInputIssue(field.value, this.measurementKind(field), this.unit));
      if (bad) {
        this.showError(this.inputError(bad, measurementInputIssue(bad.value, this.measurementKind(bad), this.unit)));
        bad.setAttribute('aria-invalid', 'true'); bad.focus(); return;
      }
      this.showError('');
      const basis = this.basis && fields.every(field => !measurementInputIssue(String(this.basis[field.name]), this.measurementKind(field), this.basis.unit)) ? this.basis : this.readMeasurements();
      // Keep the submitted category and display unit with the result while
      // shoppers edit the next calculation in the form.
      const result = { ...recommend(chart, basis, basis.unit), category, unit: this.unit };
      this.lastResult = result;
      this.renderResult(result);
      this.showChart(category);
      this.renderCharts();
      this.querySelector('[data-result-title]').focus({ preventScroll: true });
      if (window.matchMedia('(max-width: 768px)').matches) this.querySelector('[data-result]').scrollIntoView({ behavior: this.motion(), block: 'center' });
    }
    renderResult(result) {
      const chart = this.data[result.category];
      const panel = this.querySelector('[data-result]');
      const isFit = result.status === 'fit';
      panel.classList.toggle('is-check', !isFit);
      this.querySelector('[data-welcome]').hidden = true;
      panel.hidden = false;
      this.text('[data-result-eyebrow]', isFit ? 'YOUR FITWARM FIT' : 'LET’S TAKE A CLOSER LOOK');
      this.text('[data-result-title]', isFit ? 'Suggested size' : 'Outside this size chart');
      this.text('[data-result-size]', isFit ? result.row.size : '');
      this.text('[data-result-category]', chart.label);
      this.text('[data-result-note]', result.note);
      this.querySelector('[data-result-note]').hidden = !result.note;
      const dimensions = this.querySelector('[data-result-dimensions]');
      dimensions.replaceChildren();
      if (isFit) {
        const column = result.unit === 'cm' ? 1 : 0;
        const pairs = result.category === 'bandana' ? [['Chart neck range', `${result.row.neckMin[column]}–${result.row.neckMax[column]} ${result.unit}`]] : [['Garment chest girth', `${result.row.chest[column]} ${result.unit}`], [`Garment ${LABELS[chart.secondary].toLowerCase()}`, `${result.row[chart.secondary][column]} ${result.unit}`]];
        pairs.forEach(([label, value]) => { const div = document.createElement('div'); const span = document.createElement('span'); span.textContent = label; const strong = document.createElement('strong'); strong.textContent = value; div.append(span, strong); dimensions.append(div); });
      }
      dimensions.hidden = !isFit;
      const link = this.querySelector('[data-shop-link]');
      link.href = isFit ? chart.url : '/pages/fitwarm-support';
      link.firstChild.textContent = isFit ? 'Explore the collection ' : 'Get sizing help ';
      this.text('[data-chart-status]', isFit ? `Your suggested size ${result.row.size} is highlighted in the ${chart.short.toLowerCase()} chart.` : '');
    }
    showChart(category) {
      this.chartCategory = category;
      this.querySelectorAll('[data-chart-category]').forEach(button => { const active = button.dataset.chartCategory === category; button.setAttribute('aria-selected', String(active)); button.tabIndex = active ? 0 : -1; });
      this.querySelectorAll('[data-chart-panel]').forEach(panel => { panel.hidden = panel.dataset.chartPanel !== category; });
      const result = this.lastResult;
      this.text('[data-chart-status]', result && result.status === 'fit' && result.category === category ? `Your suggested size ${result.row.size} is highlighted below.` : '');
    }
    renderCharts() {
      const column = this.chartUnit === 'cm' ? 1 : 0;
      for (const [key, chart] of Object.entries(this.data)) {
        const body = this.querySelector(`[data-chart-body="${key}"]`);
        body.replaceChildren();
        chart.rows.forEach(row => {
          const tr = document.createElement('tr');
          const th = document.createElement('th'); th.scope = 'row'; th.textContent = row.size; tr.append(th);
          const recommended = this.lastResult && this.lastResult.status === 'fit' && this.lastResult.category === key && this.lastResult.row.size === row.size;
          if (recommended) { tr.classList.add('is-recommended'); const badge = document.createElement('span'); badge.className = 'fw-size__row-tag'; badge.textContent = 'Your suggestion'; th.append(badge); }
          const values = key === 'bandana' ? [`${row.neckMin[column]}–${row.neckMax[column]}`] : [row.chest[column], row[chart.secondary][column]];
          values.forEach(value => { const td = document.createElement('td'); td.textContent = `${value} ${this.chartUnit}`; tr.append(td); });
          body.append(tr);
        });
      }
    }
    renderBreedOptions() {
      this.querySelector('[data-breed-clear]').hidden = !this.querySelector('[data-breed-search]').value;
      const query = normalize(this.querySelector('[data-breed-search]').value);
      const alias = ALIASES[query];
      const popular = ['Chihuahua', 'Pomeranian', 'Shih Tzu', 'Miniature Dachshund', 'Pug', 'French Bulldog'];
      const matches = query ? this.breeds.filter(name => normalize(name).includes(query) || (alias && name === alias)) : popular;
      const options = this.querySelector('[data-breed-options]'); options.replaceChildren();
      this.querySelector('[data-popular-label]').hidden = Boolean(query);
      matches.slice(0, 8).forEach(name => {
        const button = document.createElement('button'); button.type = 'button'; button.textContent = name;
        button.setAttribute('aria-pressed', String(name === this.selectedBreed));
        button.addEventListener('click', () => {
          this.selectedBreed = name;
          this.querySelector('[data-breed-search]').value = name;
          this.renderBreedOptions();
          this.renderBreedResult();
        }, { signal: this.abort.signal });
        options.append(button);
      });
      this.text('[data-breed-status]', query ? matches.length ? `${matches.length} matching ${matches.length === 1 ? 'breed' : 'breeds'}${matches.length > 8 ? '; showing the first 8. Keep typing to narrow the list.' : '.'}` : 'No matching breed in this guide. Use their measurements in the size finder above.' : '');
    }
    renderBreedResult() {
      if (!this.selectedBreed) return;
      const chart = this.data[this.querySelector('[data-breed-category]').value];
      const sizes = breedSizes(chart, this.selectedBreed);
      this.querySelector('[data-breed-result]').hidden = false;
      this.text('[data-breed-name]', this.selectedBreed);
      this.text('[data-breed-description]', sizes.length ? `${chart.short} · starting ${sizes.length === 1 ? 'size' : 'sizes'}` : 'No published breed suggestion for this category. Use measurements to check the fit.');
      this.text('[data-breed-sizes]', sizes.length ? sizes.join(' / ') : 'Measure first');
    }
  }
  customElements.define('fitwarm-size-guide', FitwarmSizeGuide);
})();
